import { queryClient } from '$/services/client';
import { backendEndpoints } from '$/services/endpoints';
import { fetcher } from '$/services/fetcher';
import { GeneralBackendResponseObjectSchema } from '$/services/mapper/authentication';
import { validateToken } from '$/services/usecases/authentication';
import { GenerateSchoolVerificationFileLinkRequestSchema } from '$/services/usecases/invitation/mapper/invitation';
import { teacherInvitationsQuery } from '$/services/usecases/invitation/queries';
import {
  AcceptTeacherInvitationResponseSchema,
  GetSchoolByIdResponseSchema,
  GetSchoolsResponseSchema,
  GetSchoolTeachersResponseSchema,
} from '$/services/usecases/school/mapper/schools';
import { schoolQuery } from '$/services/usecases/school/queries';

export const getAllSchools = async (signal: AbortSignal) => {
  await validateToken();
  return fetcher(backendEndpoints.SCHOOLS_URL, {
    method: 'GET',
    includeCredentials: true,
    parser: (data) => GetSchoolsResponseSchema.parse(data),
    signal,
  });
};

export const getSchoolById = async (signal: AbortSignal, id: string) => {
  await validateToken();
  return fetcher(`${backendEndpoints.SCHOOLS_URL}/${id}`, {
    method: 'GET',
    includeCredentials: true,
    parser: (data) => GetSchoolByIdResponseSchema.parse(data),
    signal,
  });
};

export const generateSchoolVerificationFileLink = async (id: string) => {
  await validateToken();
  return fetcher(
    `${backendEndpoints.SCHOOLS_URL}/${id}/generateVerificationFileLink`,
    {
      method: 'GET',
      parser: (data) =>
        GenerateSchoolVerificationFileLinkRequestSchema.parse(data),
      includeCredentials: true,
    },
  );
};

export const updateSchool = async (payload: {
  id: string;
  name?: string;
  department?: string;
  address?: string;
  website?: string;
  phone?: string;
  deputyId?: string | null;
}) => {
  await validateToken();
  const { id, ...body } = payload;
  const response = await fetcher(`${backendEndpoints.SCHOOLS_URL}/${id}`, {
    method: 'PATCH',
    body,
    includeCredentials: true,
    parser: (data) => GeneralBackendResponseObjectSchema.parse(data),
  });

  if (response.isSuccessful) {
    await queryClient.invalidateQueries({
      queryKey: schoolQuery(id).queryKey,
    });
  }

  return response;
};

export const getTeachersBySchoolId = async (
  signal: AbortSignal,
  id: string,
) => {
  await validateToken();
  return fetcher(`${backendEndpoints.SCHOOLS_URL}/${id}/teachers`, {
    method: 'GET',
    includeCredentials: true,
    parser: (data) => GetSchoolTeachersResponseSchema.parse(data),
    signal,
  });
};

export const acceptTeacherInvitation = async (payload: {
  id: string;
  password?: string;
}) => {
  return fetcher(
    `${backendEndpoints.SCHOOLS_URL}/teachers/acceptInvitation/${payload.id}`,
    {
      method: 'POST',
      body: { password: payload.password },
      includeCredentials: true,
      parser: (data) => AcceptTeacherInvitationResponseSchema.parse(data),
    },
  );
};

export const extendTeacherExpiry = async (payload: {
  schoolId: string;
  teacherId: string;
}) => {
  await validateToken();
  const response = await fetcher(
    `${backendEndpoints.SCHOOLS_URL}/teachers/${payload.teacherId}/extendExpiry`,
    {
      method: 'POST',
      body: {},
      includeCredentials: true,
      parser: (data) => GeneralBackendResponseObjectSchema.parse(data),
    },
  );

  if (response.isSuccessful) {
    await queryClient.invalidateQueries({
      queryKey: ['schools', payload.schoolId, 'teachers'],
    });
  }

  return response;
};

export const deleteTeacher = async (payload: {
  schoolId: string;
  teacherId: string;
}) => {
  await validateToken();
  const response = await fetcher(
    `${backendEndpoints.SCHOOLS_URL}/${payload.schoolId}/teachers/${payload.teacherId}`,
    {
      method: 'DELETE',
      includeCredentials: true,
      parser: (data) => GeneralBackendResponseObjectSchema.parse(data),
    },
  );

  if (response.isSuccessful) {
    await queryClient.invalidateQueries({
      queryKey: ['schools', payload.schoolId, 'teachers'],
    });
  }

  return response;
};

export const deleteSchool = async (id: string) => {
  await validateToken();
  return fetcher(`${backendEndpoints.SCHOOLS_URL}/${id}`, {
    method: 'DELETE',
    includeCredentials: true,
    parser: (data) => GeneralBackendResponseObjectSchema.parse(data),
  });
};

export const inviteTeacher = async (payload: {
  schoolId: string;
  email: string;
  firstName: string;
  lastName: string;
}) => {
  const { schoolId, ...body } = payload;
  await validateToken();
  const response = await fetcher(
    `${backendEndpoints.SCHOOLS_URL}/${schoolId}/teachers/invite`,
    {
      method: 'POST',
      body,
      includeCredentials: true,
      parser: (data) => GeneralBackendResponseObjectSchema.parse(data),
    },
  );

  if (response.isSuccessful) {
    await queryClient.invalidateQueries({
      queryKey: teacherInvitationsQuery.queryKey,
    });
  }

  return response;
};
